import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { fetchTerugkomMoment, setTerugkomMoment, type TerugkomStand } from '../../lib/offertes';
import { formatDate } from '../../lib/format';

interface Props {
  leadId: string;
  /** Huidige Lead.Status; de pill nodigt alleen uit bij uitstel, maar toont een gezette afspraak altijd. */
  status: string;
  className?: string;
}

const UITSTEL = new Set(['Gereageerd: On Hold', 'Gereageerd: Komt er snel op terug']);

/**
 * Terugkom-moment op de offerte: "wij mailen de offerte opnieuw op ...".
 * Leest en schrijft via /api/offertes/terugkom (zelfde primitief als het
 * mail-verb sluit_lead). Een datum in het verleden met verstuurd-stempel is
 * de bevestiging dat de herinnering is gegaan; zonder stempel staat hij nog
 * in de wachtrij van de cron.
 */
export function TerugkomMomentPill({ leadId, status, className }: Props) {
  const [stand, setStand] = useState<TerugkomStand | null>(null);
  const [open, setOpen] = useState(false);
  const [datum, setDatum] = useState('');
  const [context, setContext] = useState('');
  const [redenKlant, setRedenKlant] = useState('');
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const laad = useCallback(() => {
    fetchTerugkomMoment(leadId)
      .then((s) => {
        setStand(s);
        setDatum(s?.terugkomOp ?? '');
        setContext(s?.terugkomContext ?? '');
        setRedenKlant(s?.terugkomRedenKlant ?? '');
      })
      .catch(() => setStand(null));
  }, [leadId]);

  useEffect(() => {
    laad();
  }, [laad]);

  const bewaar = useCallback(
    async (nieuw: string | null) => {
      setBusy(true);
      setFout(null);
      try {
        await setTerugkomMoment(leadId, nieuw, nieuw ? context : null, nieuw ? redenKlant : null);
        setOpen(false);
        laad();
      } catch (err) {
        setFout(err instanceof Error ? err.message : 'Opslaan mislukt');
      } finally {
        setBusy(false);
      }
    },
    [leadId, context, redenKlant, laad],
  );

  const gezet = !!stand?.terugkomOp;
  const verstuurd = !!stand?.terugkomVerstuurdAt;
  if (!gezet && !UITSTEL.has(status)) return null;

  const minimum = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);

  return (
    <div className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={stand?.terugkomContext || 'Terugkom-moment: wij mailen de offerte die dag opnieuw met actuele prijs'}
        className={clsx(
          'text-xs font-medium rounded px-2 py-0.5 cursor-pointer',
          'focus:outline-none focus:ring-1 focus:ring-indigo-500',
          verstuurd
            ? 'bg-emerald-100 text-emerald-700'
            : gezet
              ? 'bg-violet-100 text-violet-700'
              : 'bg-slate-100 text-slate-500 border border-dashed border-slate-300',
        )}
      >
        {verstuurd
          ? `Herinnerd ${formatDate(stand!.terugkomVerstuurdAt)}`
          : gezet
            ? `Terugkomen ${formatDate(stand!.terugkomOp)}`
            : '+ Terugkom-moment'}
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-72 rounded-lg border border-slate-200 bg-white p-3 shadow-lg text-xs">
          <label className="block text-slate-500 mb-1">Wij mailen de offerte opnieuw op</label>
          <input
            type="date"
            min={minimum}
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <label className="block text-slate-500 mt-2 mb-1">In de woorden van de klant (opent de mail)</label>
          <input
            type="text"
            value={redenKlant}
            maxLength={120}
            placeholder='bv. "na je vakantie in november"'
            onChange={(e) => setRedenKlant(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <p className="mt-1 text-slate-400">Wordt: "Je liet ons weten dat je je vloerkleed … wilde laten reinigen."</p>
          <label className="block text-slate-500 mt-2 mb-1">Context voor kantoor (optioneel)</label>
          <input
            type="text"
            value={context}
            maxLength={500}
            placeholder='bv. "hoogpolig wollen karpet, na verhuizing"'
            onChange={(e) => setContext(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
          {fout && <p className="mt-2 text-red-600">{fout}</p>}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={busy || !datum}
              onClick={() => bewaar(datum)}
              className="rounded bg-indigo-600 px-2.5 py-1 text-white disabled:opacity-50"
            >
              Opslaan
            </button>
            {gezet && (
              <button type="button" disabled={busy} onClick={() => bewaar(null)} className="text-slate-500 hover:text-slate-700">
                Afspraak wissen
              </button>
            )}
            <button type="button" onClick={() => setOpen(false)} className="ml-auto text-slate-400 hover:text-slate-600">
              Sluiten
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
